import { useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import {
  AIActionUsageStatsDto,
  AIActionUsageStatsResponseDto,
} from '@/Domain/api/Action/dto/Action.dto';
import { formatRelativeTime } from '@/Domain/helper/RelativeTime.helper';

type Props = {
  actionId: number;
};

const StatCard = ({
  label,
  children,
  dot,
  testId,
}: {
  label: string;
  children: ReactNode;
  dot?: string;
  testId?: string;
}) => (
  <div className="p-4 rounded-xl bg-muted border border-border" data-testid={testId}>
    <p className="text-xs text-foreground-subtle font-medium uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-semibold text-foreground mt-1 flex items-center gap-2">
      {dot && <span className={`inline-block size-2 rounded-full ${dot}`} />}
      {children}
    </p>
  </div>
);

const StatsGrid = ({ stats }: { stats: AIActionUsageStatsDto }) => {
  const { t } = useTranslation('actions_page', { keyPrefix: 'stats' });

  const lastCalled = stats.last_called_at ? formatRelativeTime(stats.last_called_at) : null;
  const footer = lastCalled
    ? `${t('last_called')}: ${lastCalled} \u00b7 ${t('tooltip_window')}`
    : t('tooltip_window');

  return (
    <div>
      <div className="grid grid-cols-3 gap-3" data-testid="usage-stats-grid">
        <StatCard label={t('total_calls')} testId="stat-total-calls">
          {String(stats.total_calls)}
        </StatCard>
        <StatCard label={t('success_rate')} testId="stat-success-rate">
          {`${Math.round(stats.success_rate * 10) / 10}%`}
        </StatCard>
        <StatCard label={t('avg_latency')} testId="stat-avg-latency">
          {stats.avg_duration_ms != null ? `${Math.round(stats.avg_duration_ms)}ms` : '\u2014'}
        </StatCard>
        <StatCard label={t('successful')} dot="bg-emerald-500" testId="stat-successful">
          {String(stats.success_count)}
        </StatCard>
        <StatCard
          label={t('issues')}
          dot={stats.warning_count > 0 ? 'bg-amber-500' : 'bg-muted-foreground/40'}
          testId="stat-issues"
        >
          {String(stats.warning_count)}
        </StatCard>
        <StatCard
          label={t('failed')}
          dot={stats.error_count > 0 ? 'bg-red-500' : 'bg-muted-foreground/40'}
          testId="stat-failed"
        >
          {String(stats.error_count)}
        </StatCard>
      </div>
      <p
        className="text-xs text-foreground-subtle text-right mt-2"
        data-testid="usage-stats-footer"
      >
        {footer}
      </p>
    </div>
  );
};

const EmptyState = () => {
  const { t } = useTranslation('actions_page', { keyPrefix: 'stats' });

  return (
    <div
      className="border border-dashed border-border rounded-xl flex flex-col items-center justify-center py-12 gap-3"
      data-testid="usage-stats-empty"
    >
      <Activity className="size-10 text-foreground-subtle" />
      <p className="text-sm font-medium text-foreground-subtle">{t('empty_title')}</p>
      <p className="text-xs text-foreground-subtle text-center max-w-xs">
        {t('empty_description')}
      </p>
    </div>
  );
};

export const UsageStats = ({ actionId }: Props) => {
  const { data: statsData } = useQuery<AIActionUsageStatsResponseDto>({
    queryKey: [QueryKey.actions.basic.stats],
  });

  const stats = statsData?.stats?.[String(actionId)];

  if (!stats || stats.total_calls === 0) {
    return <EmptyState />;
  }

  return <StatsGrid stats={stats} />;
};
